/*
 * Custom Video Carousel – <custom-video-carousel> web component.
 * Scoped to its own element: no globals, no theme selectors, no libraries.
 * Listeners are bound in connectedCallback and removed in disconnectedCallback,
 * so the Theme Editor re-rendering the section never duplicates them.
 *
 * Playback model
 *  - Carousel videos are always silent (muted, inline, looped) and only play while on screen.
 *  - Clicking one opens a native <dialog> holding a SEPARATE <video> built from the same
 *    Shopify-hosted sources. The carousel video is never unmuted.
 *  - While a dialog is open, every carousel video on the page (all section instances) is
 *    paused; they resume on close. Only the dialog video can ever produce sound.
 */
(function () {
  if (window.customElements.get('custom-video-carousel')) return;

  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MODAL_EVENT = 'cvc:modal';
  var FOCUSABLE = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

  class CustomVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cvc-track]');
      if (!this.track) return;

      this.cards = Array.prototype.slice.call(this.track.querySelectorAll('[data-cvc-card]'));
      this.prev = this.querySelector('[data-cvc-prev]');
      this.next = this.querySelector('[data-cvc-next]');
      this.dotsWrap = this.querySelector('[data-cvc-dots]');
      this.dialog = this.querySelector('[data-cvc-modal]');
      this.popup = !!this.dialog && typeof this.dialog.showModal === 'function';
      this.autoplayMs = parseInt(this.dataset.autoplayCarousel, 10) || 0;
      this.timer = null;
      this.drag = null;
      this.suppressClick = false;
      this.pointerInside = false;
      this.suspended = false;
      this.modalVideo = null;
      this.rafId = null;

      this.bound = {
        scroll: this.onScroll.bind(this),
        prev: this.go.bind(this, -1),
        next: this.go.bind(this, 1),
        resize: this.update.bind(this),
        down: this.onPointerDown.bind(this),
        move: this.onPointerMove.bind(this),
        up: this.onPointerUp.bind(this),
        click: this.onClickCapture.bind(this),
        enter: this.onEnter.bind(this),
        leave: this.onLeave.bind(this),
        visibility: this.onVisibility.bind(this),
        blockSelect: this.onBlockSelect.bind(this),
        videoPlay: this.onVideoPlay.bind(this),
        mediaClick: this.onMediaClick.bind(this),
        dotClick: this.onDotClick.bind(this),
        modalState: this.onModalState.bind(this),
        dialogClick: this.onDialogClick.bind(this),
        dialogInput: this.onDialogInput.bind(this),
        dialogKey: this.onDialogKey.bind(this),
        dialogClose: this.onDialogClose.bind(this),
        dialogDown: this.onDialogDown.bind(this),
        scrubEnd: this.onScrubEnd.bind(this),
        fsChange: this.onFullscreenChange.bind(this)
      };
      this.scrubbing = false;
      this.downTarget = null;

      this.track.addEventListener('scroll', this.bound.scroll, { passive: true });
      this.track.addEventListener('pointerdown', this.bound.down);
      this.track.addEventListener('click', this.bound.click, true);
      this.track.addEventListener('click', this.bound.mediaClick);
      this.track.addEventListener('play', this.bound.videoPlay, true);
      this.addEventListener('mouseenter', this.bound.enter);
      this.addEventListener('mouseleave', this.bound.leave);
      this.addEventListener('focusin', this.bound.enter);
      this.addEventListener('focusout', this.bound.leave);
      this.addEventListener('shopify:block:select', this.bound.blockSelect);
      document.addEventListener('visibilitychange', this.bound.visibility);
      document.addEventListener(MODAL_EVENT, this.bound.modalState);
      if (this.prev) this.prev.addEventListener('click', this.bound.prev);
      if (this.next) this.next.addEventListener('click', this.bound.next);
      if (this.dotsWrap) this.dotsWrap.addEventListener('click', this.bound.dotClick);

      if (this.popup) {
        this.player = this.dialog.querySelector('[data-cvc-player]');
        this.dialog.addEventListener('pointerdown', this.bound.dialogDown);
        this.dialog.addEventListener('click', this.bound.dialogClick);
        this.dialog.addEventListener('input', this.bound.dialogInput);
        this.dialog.addEventListener('keydown', this.bound.dialogKey);
        this.dialog.addEventListener('close', this.bound.dialogClose);
        document.addEventListener('fullscreenchange', this.bound.fsChange);
        document.addEventListener('webkitfullscreenchange', this.bound.fsChange);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.bound.resize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.bound.resize);
      }

      this.setupVideoObserver();
      this.update();
      this.startAutoplay();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      if (!this.track) return;
      // Theme Editor re-render while the popup is open: release everything first
      if (this.modalVideo) this.cleanupModal(false);
      this.stopAutoplay();
      cancelAnimationFrame(this.rafId);
      this.track.removeEventListener('scroll', this.bound.scroll);
      this.track.removeEventListener('pointerdown', this.bound.down);
      this.track.removeEventListener('click', this.bound.click, true);
      this.track.removeEventListener('click', this.bound.mediaClick);
      this.track.removeEventListener('play', this.bound.videoPlay, true);
      this.removeEventListener('mouseenter', this.bound.enter);
      this.removeEventListener('mouseleave', this.bound.leave);
      this.removeEventListener('focusin', this.bound.enter);
      this.removeEventListener('focusout', this.bound.leave);
      this.removeEventListener('shopify:block:select', this.bound.blockSelect);
      document.removeEventListener('visibilitychange', this.bound.visibility);
      document.removeEventListener(MODAL_EVENT, this.bound.modalState);
      window.removeEventListener('resize', this.bound.resize);
      document.removeEventListener('pointermove', this.bound.move);
      document.removeEventListener('pointerup', this.bound.up);
      document.removeEventListener('pointercancel', this.bound.up);
      if (this.prev) this.prev.removeEventListener('click', this.bound.prev);
      if (this.next) this.next.removeEventListener('click', this.bound.next);
      if (this.dotsWrap) this.dotsWrap.removeEventListener('click', this.bound.dotClick);
      if (this.popup) {
        this.dialog.removeEventListener('pointerdown', this.bound.dialogDown);
        this.dialog.removeEventListener('click', this.bound.dialogClick);
        this.dialog.removeEventListener('input', this.bound.dialogInput);
        this.dialog.removeEventListener('keydown', this.bound.dialogKey);
        this.dialog.removeEventListener('close', this.bound.dialogClose);
        document.removeEventListener('fullscreenchange', this.bound.fsChange);
        document.removeEventListener('webkitfullscreenchange', this.bound.fsChange);
        document.removeEventListener('pointerup', this.bound.scrubEnd);
        document.removeEventListener('pointercancel', this.bound.scrubEnd);
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.videoObserver) this.videoObserver.disconnect();
    }

    /* ---------- geometry ---------- */

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    get step() {
      var first = this.cards[0];
      var second = this.cards[1];
      if (!first) return this.track.clientWidth;
      if (second) return second.offsetLeft - first.offsetLeft;
      return first.offsetWidth;
    }

    get pageCount() {
      if (!this.maxScroll) return 1;
      return Math.ceil(this.track.scrollWidth / this.track.clientWidth);
    }

    smooth() {
      return reduceMotionQuery.matches ? 'auto' : 'smooth';
    }

    /* ---------- navigation ---------- */

    go(direction) {
      // Arrows move one page worth of cards; carousel autoplay (tick) moves a single card
      var distance = Math.max(this.step, Math.floor(this.track.clientWidth / this.step) * this.step) * direction;
      this.track.scrollBy({ left: distance, behavior: this.smooth() });
      this.restartAutoplay();
    }

    goToPage(index) {
      var pages = this.pageCount;
      var target = pages > 1 ? (this.maxScroll * index) / (pages - 1) : 0;
      this.track.scrollTo({ left: target, behavior: this.smooth() });
      this.restartAutoplay();
    }

    onDotClick(event) {
      var dot = event.target.closest('[data-cvc-dot]');
      if (dot) this.goToPage(parseInt(dot.dataset.cvcDot, 10));
    }

    onScroll() {
      cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(this.updateState.bind(this));
    }

    update() {
      this.buildDots();
      this.updateState();
    }

    buildDots() {
      if (!this.dotsWrap) return;
      var pages = this.pageCount;
      if (this.dotsWrap.children.length === pages) return;
      var html = '';
      for (var i = 0; i < pages; i++) {
        html += '<button type="button" class="cvc__dot" data-cvc-dot="' + i + '" aria-label="' +
          (this.dataset.dotLabel || 'Go to page') + ' ' + (i + 1) + '"></button>';
      }
      this.dotsWrap.innerHTML = html;
      this.dotsWrap.hidden = pages < 2;
    }

    updateState() {
      var max = this.maxScroll;
      var left = this.track.scrollLeft;
      var scrollable = max > 1;

      if (this.prev) {
        this.prev.hidden = !scrollable;
        this.prev.disabled = left <= 1;
      }
      if (this.next) {
        this.next.hidden = !scrollable;
        this.next.disabled = left >= max - 1;
      }
      if (this.dotsWrap) {
        var pages = this.pageCount;
        var active = max ? Math.round((left / max) * (pages - 1)) : 0;
        var dots = this.dotsWrap.children;
        for (var i = 0; i < dots.length; i++) {
          if (i === active) dots[i].setAttribute('aria-current', 'true');
          else dots[i].removeAttribute('aria-current');
        }
        this.dotsWrap.hidden = pages < 2;
      }
    }

    onBlockSelect(event) {
      var card = event.target.closest && event.target.closest('[data-cvc-card]');
      if (card) this.track.scrollTo({ left: card.offsetLeft - this.track.offsetLeft, behavior: 'auto' });
    }

    /* ---------- carousel autoplay ---------- */

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayMs || reduceMotionQuery.matches || this.cards.length < 2) return;
      this.timer = window.setInterval(this.tick.bind(this), this.autoplayMs);
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      if (this.timer) this.startAutoplay();
    }

    tick() {
      if (this.pointerInside || this.drag || this.suspended || document.hidden || !this.maxScroll) return;
      if (this.track.scrollLeft >= this.maxScroll - 1) {
        this.track.scrollTo({ left: 0, behavior: this.smooth() });
      } else {
        this.track.scrollBy({ left: this.step, behavior: this.smooth() });
      }
    }

    onEnter() {
      this.pointerInside = true;
    }

    onLeave() {
      this.pointerInside = false;
    }

    onVisibility() {
      if (document.hidden) this.pauseAllVideos();
      else this.resumeVideos();
    }

    /* ---------- mouse drag (touch uses native scrolling) ---------- */

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('video[controls], input, button:not([data-cvc-open])')) return;
      this.drag = { x: event.clientX, left: this.track.scrollLeft, moved: false };
      document.addEventListener('pointermove', this.bound.move);
      document.addEventListener('pointerup', this.bound.up);
      document.addEventListener('pointercancel', this.bound.up);
    }

    onPointerMove(event) {
      if (!this.drag) return;
      var delta = event.clientX - this.drag.x;
      if (!this.drag.moved && Math.abs(delta) > 5) {
        this.drag.moved = true;
        this.track.classList.add('is-dragging');
      }
      if (this.drag.moved) this.track.scrollLeft = this.drag.left - delta;
    }

    onPointerUp() {
      document.removeEventListener('pointermove', this.bound.move);
      document.removeEventListener('pointerup', this.bound.up);
      document.removeEventListener('pointercancel', this.bound.up);
      if (this.drag && this.drag.moved) {
        this.suppressClick = true;
        window.setTimeout(function () { this.suppressClick = false; }.bind(this), 0);
      }
      this.drag = null;
      this.track.classList.remove('is-dragging');
    }

    // A drag must never be interpreted as a click on a link or on the video
    onClickCapture(event) {
      if (this.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    /* ---------- carousel video behaviour ---------- */

    setupVideoObserver() {
      var self = this;
      var videos = this.track.querySelectorAll('video[data-cvc-autoplay]');
      if (!videos.length || !('IntersectionObserver' in window)) return;
      this.videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          video.cvcVisible = entry.isIntersecting;
          if (entry.isIntersecting) self.playVideo(video);
          else video.pause();
        });
      }, { threshold: 0.6 });
      videos.forEach(function (video) { self.videoObserver.observe(video); });
    }

    playVideo(video) {
      if (this.suspended || reduceMotionQuery.matches || document.hidden) return;
      video.muted = true; // carousel videos are never audible
      if (video.preload === 'none') video.preload = 'metadata';
      var promise = video.play();
      if (promise && promise.catch) promise.catch(function () {});
    }

    pauseAllVideos() {
      this.track.querySelectorAll('video').forEach(function (video) { video.pause(); });
    }

    // Restart only the autoplay videos that are currently on screen
    resumeVideos() {
      var self = this;
      this.track.querySelectorAll('video[data-cvc-autoplay]').forEach(function (video) {
        if (video.cvcVisible) self.playVideo(video);
      });
    }

    // Another instance (or this one) opened/closed its popup
    onModalState(event) {
      this.suspended = !!event.detail.open;
      if (this.suspended) this.pauseAllVideos();
      else this.resumeVideos();
    }

    // Only one manually played carousel video at a time
    onVideoPlay(event) {
      var current = event.target;
      if (!current || current.tagName !== 'VIDEO') return;
      if (this.suspended) {
        current.pause();
        return;
      }
      this.track.querySelectorAll('video').forEach(function (video) {
        if (video !== current && !video.paused && !video.hasAttribute('data-cvc-autoplay')) video.pause();
      });
    }

    onMediaClick(event) {
      var opener = event.target.closest('[data-cvc-open]');
      if (opener && this.popup) {
        // Must run synchronously inside the click so unmuted playback is permitted
        this.openModal(opener);
        return;
      }
      // Without popup: tap/click on a video without native controls toggles playback
      var media = event.target.closest('[data-cvc-media]');
      if (!media || event.target.closest('a, button')) return;
      var video = media.querySelector('video');
      if (!video || video.controls || media.hasAttribute('data-cvc-linked')) return;
      if (video.paused) {
        video.preload = 'auto';
        var promise = video.play();
        if (promise && promise.catch) promise.catch(function () {});
      } else {
        video.pause();
      }
    }

    /* ---------- popup ---------- */

    openModal(opener) {
      if (this.dialog.open) return;
      var media = opener.closest('[data-cvc-media]');
      var source = media && media.querySelector('video');
      if (!source) return;

      this.opener = opener;
      var video = document.createElement('video');
      video.className = 'cvc-modal__video';
      video.playsInline = true;
      video.preload = 'auto';
      video.loop = this.dataset.popupLoop === 'true';
      video.setAttribute('aria-label', source.getAttribute('aria-label') || 'Video');
      if (source.poster) video.poster = source.poster;
      Array.prototype.forEach.call(source.querySelectorAll('source'), function (node) {
        video.appendChild(node.cloneNode(true));
      });
      video.muted = false;
      // iOS ignores programmatic volume: hide the slider there instead of showing a dead control
      video.volume = 0.5;
      var volumeWorks = video.volume === 0.5;
      video.volume = 1;
      this.dialog.querySelector('[data-cvc-volume]').hidden = !volumeWorks;
      this.modalVideo = video;
      this.player.appendChild(video);

      var ratio = source.videoWidth && source.videoHeight ? source.videoWidth / source.videoHeight : 0;
      this.dialog.style.setProperty('--ar', ratio || this.dataset.ratio || 0.5625);
      this.dialog.setAttribute('aria-label', video.getAttribute('aria-label'));

      this.onVideoEvent = this.syncControls.bind(this);
      this.onVideoMeta = function () {
        if (video.videoWidth && video.videoHeight) {
          this.dialog.style.setProperty('--ar', video.videoWidth / video.videoHeight);
        }
        this.syncControls();
      }.bind(this);
      ['play', 'pause', 'volumechange', 'timeupdate', 'seeked', 'durationchange', 'ended'].forEach(function (name) {
        video.addEventListener(name, this.onVideoEvent);
      }, this);
      video.addEventListener('loadedmetadata', this.onVideoMeta);
      video.addEventListener('loadeddata', this.onVideoEvent);
      video.addEventListener('playing', this.onVideoEvent);

      // Silence and pause everything behind the dialog before sound starts
      document.dispatchEvent(new CustomEvent(MODAL_EVENT, { detail: { open: true } }));
      this.lockScroll(true);
      this.dialog.showModal();
      this.syncControls();

      var promise = video.play();
      if (promise && promise.catch) {
        promise.catch(function () {
          // Browser refused audible playback: fall back to muted playback the viewer can unmute
          if (this.modalVideo !== video) return;
          video.muted = true;
          var retry = video.play();
          if (retry && retry.catch) retry.catch(function () { this.syncControls(); }.bind(this));
          this.syncControls();
        }.bind(this));
      }
    }

    closeModal() {
      if (this.dialog.open) this.dialog.close(); // fires "close" -> cleanup
      else if (this.modalVideo) this.cleanupModal(true);
    }

    onDialogClose() {
      if (this.modalVideo) this.cleanupModal(true);
    }

    // Stops playback, releases the media resource and restores the carousel
    cleanupModal(restoreFocus) {
      var video = this.modalVideo;
      this.modalVideo = null;
      if (!video) return;

      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(function () {});
      }
      ['play', 'pause', 'volumechange', 'timeupdate', 'seeked', 'durationchange', 'ended'].forEach(function (name) {
        video.removeEventListener(name, this.onVideoEvent);
      }, this);
      video.removeEventListener('loadedmetadata', this.onVideoMeta);
      video.removeEventListener('loadeddata', this.onVideoEvent);
      video.removeEventListener('playing', this.onVideoEvent);
      this.onScrubEnd();
      video.pause();
      while (video.firstChild) video.removeChild(video.firstChild);
      video.removeAttribute('src');
      video.load();
      video.remove();

      if (this.dialog.open) this.dialog.close();
      this.lockScroll(false);
      document.dispatchEvent(new CustomEvent(MODAL_EVENT, { detail: { open: false } }));

      if (restoreFocus && this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    lockScroll(lock) {
      var root = document.documentElement;
      if (lock) {
        this.previousOverflow = root.style.overflow;
        root.style.overflow = 'hidden';
      } else {
        root.style.overflow = this.previousOverflow || '';
      }
    }

    onDialogDown(event) {
      this.downTarget = event.target;
      if (event.target.hasAttribute && event.target.hasAttribute('data-cvc-seek')) {
        // Hold the thumb still while the viewer drags it; timeupdate must not fight the pointer
        this.scrubbing = true;
        document.addEventListener('pointerup', this.bound.scrubEnd);
        document.addEventListener('pointercancel', this.bound.scrubEnd);
      }
    }

    onScrubEnd() {
      this.scrubbing = false;
      document.removeEventListener('pointerup', this.bound.scrubEnd);
      document.removeEventListener('pointercancel', this.bound.scrubEnd);
      this.syncControls();
    }

    onDialogClick(event) {
      var target = event.target;
      var downTarget = this.downTarget;
      this.downTarget = null;
      // Backdrop = the dialog itself. A drag that started on a slider and was released outside the
      // frame also reports the dialog as click target, so the press must have started there too.
      if (target === this.dialog) {
        if (!downTarget || downTarget === this.dialog) this.closeModal();
        return;
      }
      var action = target.closest('[data-cvc-action]');
      if (action) {
        this.runAction(action.dataset.cvcAction);
      } else if (target === this.modalVideo) {
        this.runAction('toggle');
      }
    }

    runAction(name) {
      var video = this.modalVideo;
      if (name === 'close') return this.closeModal();
      if (!video) return;
      if (name === 'toggle') {
        if (video.paused || video.ended) {
          var promise = video.play();
          if (promise && promise.catch) promise.catch(function () {});
        } else {
          video.pause();
        }
      } else if (name === 'mute') {
        video.muted = !video.muted;
        if (!video.muted && video.volume === 0) video.volume = 1;
      } else if (name === 'fullscreen') {
        this.toggleFullscreen();
      }
    }

    seekBy(seconds) {
      var video = this.modalVideo;
      if (!video || !isFinite(video.duration)) return;
      video.currentTime = Math.min(video.duration, Math.max(0, video.currentTime + seconds));
      this.syncControls();
    }

    onFullscreenChange() {
      var button = this.dialog && this.dialog.querySelector('[data-cvc-action="fullscreen"]');
      if (button) button.setAttribute('aria-pressed', document.fullscreenElement || document.webkitFullscreenElement ? 'true' : 'false');
    }

    formatTime(seconds) {
      if (!isFinite(seconds) || seconds < 0) seconds = 0;
      var total = Math.floor(seconds);
      var h = Math.floor(total / 3600);
      var m = Math.floor((total % 3600) / 60);
      var s = total % 60;
      return (h ? h + ':' + (m < 10 ? '0' : '') : '') + m + ':' + (s < 10 ? '0' : '') + s;
    }

    toggleFullscreen() {
      var frame = this.dialog.querySelector('[data-cvc-frame]');
      var video = this.modalVideo;
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      } else if (frame.requestFullscreen) {
        frame.requestFullscreen().catch(function () {});
      } else if (frame.webkitRequestFullscreen) {
        frame.webkitRequestFullscreen();
      } else if (video && video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen(); // iOS Safari only supports fullscreen on the video element
      }
    }

    onDialogInput(event) {
      var video = this.modalVideo;
      if (!video) return;
      var input = event.target;
      if (input.hasAttribute('data-cvc-volume')) {
        video.volume = parseFloat(input.value);
        video.muted = video.volume === 0;
      } else if (input.hasAttribute('data-cvc-seek')) {
        if (!isFinite(video.duration) || !video.duration) return; // metadata not loaded yet
        video.currentTime = (parseFloat(input.value) / 1000) * video.duration;
        this.syncControls();
      }
    }

    syncControls() {
      var video = this.modalVideo;
      if (!video) return;
      var toggle = this.dialog.querySelector('[data-cvc-action="toggle"]');
      var mute = this.dialog.querySelector('[data-cvc-action="mute"]');
      var volume = this.dialog.querySelector('[data-cvc-volume]');
      var seek = this.dialog.querySelector('[data-cvc-seek]');
      var playing = !video.paused && !video.ended;
      toggle.setAttribute('data-state', playing ? 'playing' : 'paused');
      toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
      mute.setAttribute('data-state', video.muted || video.volume === 0 ? 'muted' : 'on');
      mute.setAttribute('aria-label', video.muted ? mute.dataset.labelUnmute : mute.dataset.labelMute);
      volume.value = video.muted ? 0 : video.volume;
      volume.style.setProperty('--fill', (parseFloat(volume.value) * 100) + '%');

      var duration = isFinite(video.duration) ? video.duration : 0;
      var current = this.dialog.querySelector('[data-cvc-current]');
      var total = this.dialog.querySelector('[data-cvc-duration]');
      if (current) current.textContent = this.formatTime(video.currentTime);
      if (total) total.textContent = this.formatTime(duration);
      if (duration) {
        // While dragging, the input is the source of truth
        if (!this.scrubbing) seek.value = Math.round((video.currentTime / duration) * 1000);
        seek.style.setProperty('--fill', (parseFloat(seek.value) / 10) + '%');
        seek.setAttribute('aria-valuetext', this.formatTime(video.currentTime) + ' of ' + this.formatTime(duration));
      }
    }

    // Keyboard: keep Tab inside the dialog and let Space/K toggle playback
    onDialogKey(event) {
      if (event.key === 'Tab') {
        var items = Array.prototype.filter.call(this.dialog.querySelectorAll(FOCUSABLE), function (node) {
          return node.offsetParent !== null;
        });
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (!this.modalVideo || event.ctrlKey || event.metaKey || event.altKey) return;

      var target = event.target;
      var isInput = target.tagName === 'INPUT';
      var onSeek = isInput && target.hasAttribute('data-cvc-seek');
      var key = event.key;
      var video = this.modalVideo;

      // Native range steps are 0.1% of the video: use 5s steps on the seek bar instead
      if ((key === 'ArrowLeft' || key === 'ArrowRight') && (!isInput || onSeek)) {
        event.preventDefault();
        this.seekBy(key === 'ArrowLeft' ? -5 : 5);
      } else if ((key === 'ArrowUp' || key === 'ArrowDown') && !isInput) {
        event.preventDefault();
        video.volume = Math.min(1, Math.max(0, video.volume + (key === 'ArrowUp' ? 0.1 : -0.1)));
        video.muted = video.volume === 0;
      } else if (key === ' ' && !target.closest('button, input')) {
        event.preventDefault();
        this.runAction('toggle');
      } else if (key === 'k' || key === 'K') {
        this.runAction('toggle');
      } else if (key === 'm' || key === 'M') {
        this.runAction('mute');
      } else if (key === 'f' || key === 'F') {
        this.runAction('fullscreen');
      }
    }
  }

  window.customElements.define('custom-video-carousel', CustomVideoCarousel);
})();
